import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Phone, ArrowRight, UtensilsCrossed, ScrollText } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";
import { MobileActionBar } from "@/components/MobileActionBar";
import { CelebrityWall } from "@/components/CelebrityWall";
import { FadeUp, MaskedLines, ChapterHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MANIFESTO = [
  {
    num: "01",
    title: "Mâncare cinstită",
    text: "Rețetele bunicii lui Nea Mitică, neschimbate din 1987. Sarmale rulate pe loc, aluat dospit 48 de ore, cuptor cu lemne adus din Italia. N-am schimbat nimic. Nici măcar pătrunjelul.",
  },
  {
    num: "02",
    title: "Voie bună",
    text: "Aici chelnerul știe glume, cuptorul știe meserie, iar tu pleci cu zâmbetul și, foarte probabil, cu papanașii la pachet. Foamea e treabă serioasă — noi o tratăm cu veselie.",
  },
  {
    num: "03",
    title: "Două adrese, un suflet",
    text: "Vis-a-vis de Grădina Zoologică ți se fac sarmale și ciolan afumat. La Unirii, pizza se frige în cuptorul cu lemne și pastele se fac în casă. Alege-ți pofta — noi le avem pe ambele.",
  },
];

const PortalCard = ({ r, i }) => {
  const navigate = useNavigate();
  return (
    <FadeUp delay={i * 0.15}>
      <motion.button
        data-testid={`card-restaurant-${r.id}`}
        onClick={() => navigate(`/${r.id}`)}
        whileHover="hover"
        className="group relative block h-[500px] w-full overflow-hidden rounded-3xl text-left sm:h-[560px]"
      >
        <motion.img
          src={r.hero_image}
          alt={r.name}
          variants={{ hover: { scale: 1.06 } }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121110] via-[#121110]/55 to-transparent" />
        <motion.div
          variants={{ hover: { rotate: -2, y: -8 } }}
          transition={{ duration: 0.6 }}
          className="absolute left-6 top-6 h-28 w-28 overflow-hidden rounded-2xl shadow-2xl ring-4 ring-[#FAF6F0]/90 sm:h-32 sm:w-32"
        >
          <img src={r.logo} alt={`Logo ${r.name}`} className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
          <span data-testid={`badge-${r.id}`} className="inline-block rounded-full border border-red-500/60 bg-red-600/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-red-300 backdrop-blur-sm">
            {r.badge}
          </span>
          <h3 className="font-display mt-4 text-3xl font-bold leading-tight text-[#FAF6F0] sm:text-4xl">
            {r.name}
          </h3>
          <p className="font-hand mt-2 text-2xl text-red-300">{r.tagline}</p>
          <div className="mt-5 space-y-2 text-sm text-stone-300">
            <p className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-red-400" /> {r.address}
            </p>
            <p className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-red-400" /> {r.phone}
            </p>
          </div>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3 text-sm font-bold text-white transition-colors group-hover:bg-red-500">
            Intră în poveste
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </motion.button>
    </FadeUp>
  );
};

const Home = () => {
  const [restaurants, setRestaurants] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  useEffect(() => {
    axios.get(`${API}/restaurants`).then((res) => setRestaurants(res.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#121110] text-[#FAF6F0]">
      <Navbar />

      <section ref={heroRef} data-testid="portal-hero" className="grain relative flex min-h-screen flex-col justify-center overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src="/assets/hero-toast.jpg" alt="" className="h-[110%] w-full object-cover" />
        </motion.div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#121110]/75 via-[#121110]/45 to-[#121110]" />
        <p className="font-display outline-text pointer-events-none absolute -right-4 top-24 select-none whitespace-nowrap text-[16vw] font-black leading-none">
          NEA MITICĂ
        </p>
        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-5 pt-28 sm:px-8">
          <motion.img
            src="/assets/zoo/mitica-gratar.jpg"
            alt="Nea Mitică la grătar, în bereta roșie"
            initial={{ opacity: 0, rotate: -14, scale: 0.8 }}
            animate={{ opacity: 1, rotate: -6, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="floaty mb-8 h-28 w-28 rounded-3xl object-cover shadow-2xl ring-4 ring-red-600/60 sm:h-32 sm:w-32"
          />
          <p className="font-hand text-2xl text-red-400 sm:text-3xl">Bun venit în familia</p>
          <MaskedLines
            lines={["NEA MITICĂ", "Două case în București.", "O singură regulă: nimeni", "nu pleacă flămând."]}
            delay={0.35}
            lineClassName="font-display text-4xl font-black leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl"
          />
          <FadeUp delay={1} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-restaurante"
              onClick={() => {
                const el = document.getElementById("restaurante");
                if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -72 }) : el.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-3 rounded-full bg-red-600 px-8 py-4 text-base font-bold text-white transition-all hover:scale-[1.03] hover:bg-red-500"
            >
              <UtensilsCrossed className="h-5 w-5" />
              Alege-ți masa
            </button>
            <a
              href="/constitutie"
              data-testid="hero-cta-constitutie"
              className="inline-flex items-center gap-3 rounded-full border border-stone-500 px-8 py-4 text-base font-semibold text-stone-100 backdrop-blur-sm transition-colors hover:border-red-500 hover:text-red-400"
            >
              <ScrollText className="h-5 w-5" />
              Constituția lui Nea Mitică
            </a>
          </FadeUp>
        </motion.div>
      </section>

      <Marquee items={["SARMALE", "MĂMĂLIGĂ", "MICI", "PIZZA LA CUPTOR", "PASTE PROASPETE", "PAPANAȘI", "TIRAMISU", "VOIE BUNĂ"]} />

      <section id="restaurante" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <ChapterHeading num="01" title="Cele două case" />
        <FadeUp className="mt-6 max-w-2xl">
          <h2 className="font-display text-3xl font-bold leading-tight sm:text-5xl">
            Unde mâncăm azi, <span className="italic text-red-400">pofticiosule</span>?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-stone-400 sm:text-lg">
            Un singur Nea Mitică, două bucătării de legendă. Apasă pe casa care ți se potrivește poftei — sau vizitează-le pe ambele, ca omul serios.
          </p>
        </FadeUp>
        <div className="mt-12 grid gap-8 lg:grid-cols-2 lg:gap-12">
          {restaurants.map((r, i) => (
            <PortalCard key={r.id} r={r} i={i} />
          ))}
        </div>
      </section>

      <section className="grain relative bg-[#FAF6F0] py-24 text-stone-800 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <ChapterHeading num="02" title="Manifestul lui Nea Mitică" />
          <div className="mt-14 space-y-16">
            {MANIFESTO.map((m, i) => (
              <FadeUp key={m.num} delay={i * 0.1} className="grid gap-6 md:grid-cols-[140px_1fr] lg:grid-cols-[220px_1fr]">
                <span data-testid={`manifesto-num-${m.num}`} className="font-display text-6xl font-black text-red-600/90 sm:text-7xl">
                  {m.num}
                </span>
                <div className="max-w-2xl">
                  <h3 className="font-display text-2xl font-bold sm:text-3xl">{m.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-stone-600 sm:text-lg">{m.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-[#121110] py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <ChapterHeading num="03" title="Filozofia din spatele beretei roșii" />
          <FadeUp className="mt-12">
            <p data-testid="philosophy-text" className="font-hand text-2xl leading-relaxed text-stone-200 sm:text-3xl">
              Filozofia din spatele beretei roșii e foarte simplă. Ai nevoie de un partener de viață precum <span className="text-red-400">doamna Dana</span>, viziune, curaj, echipă, implicare, corectitudine, adaptabilitate, atenție la detalii, noroc, optimism, empatie, <span className="text-red-400">ficat mare</span>, răbdare, ascultare activă, flexibilitate, prieteni, referințe, dușmani, eșecuri, echilibru, judecată, generozitate, umor, <span className="text-red-400">steatoză</span>, proactivitate, bune maniere, carismă, calm, <span className="text-red-400">muncă, muncă și iar muncă</span> — coroborate cu spriț, spriț și <span className="text-red-400">câte o pălincă</span>.
            </p>
            <p className="font-display mt-12 text-xl italic leading-snug text-stone-300 sm:text-2xl">
              „Persoanele care iubesc să mănânce sunt întotdeauna cele mai bune persoane."
              <span className="font-hand mt-3 block text-2xl not-italic text-red-400">— Nea Mitică</span>
            </p>
            <p className="font-hand mt-8 text-xl text-stone-500">
              Condimentat la perfecțiune și servit la temperatură optimă.
            </p>
          </FadeUp>
        </div>
      </section>

      <CelebrityWall />

      <Marquee items={["VADUL MOLDOVEI 15", "CORNELIU COPOSU 6-8", "DIN 1987", "BERETA ROȘIE", "CINSTIT ȘI BUN"]} dark />

      <Footer restaurants={restaurants} />
      <MobileActionBar />
    </div>
  );
};

export default Home;
